import isLocal from '../common/isLocal.js'
import miniPackages from '../viewer/miniPackages.js'

export default function (window, requirejs, prefetch, isBot, experiment) {
  let { performance = {} } = window
  if (!performance.measure) {
    performance = {
      mark() {},
      measure() {}
    }
  }

  if (prefetch && window.define) {
    const dummy = () => ({})
    const define = window.define
    window.define = function (...args) {
      for (let i = 0; i < args.length; ++i) {
        if (typeof args[i] === 'function') {
          args[i] = dummy
        }
      }
      return define.apply(this, args)
    }
    window.define.amd = define.amd
    const rjs = requirejs
    window.requirejs = requirejs = function (dependencies) {
      return rjs.call(this, dependencies, dummy)
    }
  }

  function notifyParent(msg) {
    try {
      window.parent.postMessage(msg, '*')
    } catch (e) {
      //ignore
    }
  }

  notifyParent('santaStart')

  function render(isServerSide, isPreview, isExternalPreview, contentCache, queryUtil, siteModel, wixBiSession, mobileView, pageInfo, config) {
    const useDS = queryUtil.isParameterTrue('ds')
    const isQaAutomation = queryUtil.isParameterTrue('isqa')
    const isTpaIntegration = queryUtil.isParameterTrue('isTpaIntegration')
    const isWixCodeIntegration = queryUtil.isParameterTrue('isWixCodeIntegration')

    function addConditionalDependencies(pkgs) {
      const map = siteModel.rendererModel.clientSpecMap
      const mapKeys = Object.keys(map)
      const shouldLoadPackageFor = applicationType => mapKeys.some(applicationId => map[applicationId].type === applicationType)

      function insert(p, marker, ...extra) {
        const pos = p.indexOf(marker)
        if (pos !== -1) {
          p.splice(pos, 0, ...extra)
        } else {
          p.push(...extra)
        }
      }

      const isWixDomain = () => window.location.hostname === 'www.wix.com'

      const isWixSites = () => isWixDomain() || queryUtil.isParameterTrue('iswixsite')

      // We check that we have wixCodeModel, because if a site is published without Wix Code,
      // And then Wix Code is added and the site is saved, the metasite is "copied" to the
      // published site as well, showing as if there's Wix Code in the published site, although
      // there isn't
      const isWixCloud = () => shouldLoadPackageFor('siteextension') && !!siteModel.rendererModel.wixCodeModel

      function shouldLoadPackageForEComMigratedSite() {
        const {publicModel: {metaSiteFlags = ''} = {}} = siteModel
        return metaSiteFlags.indexOf('EComMigrated') >= 0
      }

      function isQuickActionEnabled(quickActionName) {
	      const {siteMetaData: {quickActions: {configuration = {}} = {}} = {}} = siteModel.rendererModel
	      return !!configuration[quickActionName]
      }

      const wixappsComps = ['container', 'textArea', 'matrixGallery', 'slideShowGallery', 'comboBoxInput', 'dialogs']

      if (!experiment.isOpen('selectiveWixapps')) {
        if (isPreview || shouldLoadPackageFor('wixapps') || shouldLoadPackageFor('ecommerce') || shouldLoadPackageForEComMigratedSite()) {
          insert(pkgs, 'tpa', 'wixappsCore', 'wixappsClassics', ...wixappsComps, 'table', 'messageView')
        }
        if (isPreview || shouldLoadPackageFor('appbuilder')) {
          insert(pkgs, 'tpa', 'wixappsCore', 'wixappsBuilder', ...wixappsComps)
        }
      }

      if (useDS || isPreview) {
        pkgs.push('documentServices', 'documentServicesSchemas')
      }
      if (isPreview) {
        pkgs.push('immutable')
      }

      if (isWixSites()) {
        pkgs.push('wixSites')
      }

      if (isWixCloud() || isPreview) {
        pkgs.push('wixCode')
      }

      if (typeof Promise === 'undefined' && window && !window.Promise) {
        pkgs.push('bluebird')
      }

      if (shouldLoadPackageFor('sitemembers') ||
        siteModel.rendererModel.passwordProtectedPages && siteModel.rendererModel.passwordProtectedPages.length) {
        pkgs.push('dialogs')
      }

      if (mobileView && isQuickActionEnabled('quickActionsMenuEnabled')) {
        pkgs.push('mobileActionsMenu')
      }

      return pkgs
    }

    const handleRequireError = prefetch ?
      () => {} :
      pkgs =>
        err => {
          if (!config.urlArgs) {
            if (err.requireModules) {
              err.requireModules.forEach(module => {
                requirejs.undef(module)
              })
            }
            config.urlArgs = `c=${Date.now()}`
            requirejs.config(config)
            const errClone = Object.keys(err).reduce((r, k) => {
              r[k] = err[k]
              return r
            }, {})
            errClone.message = err.message
            errClone.stack = err.stack ? '' + err.stack : '' //eslint-disable-line prefer-template
            requirejs(pkgs, () => {
              errClone.errn = 'REQUIREJS_RETRY_ERROR'
              errClone.errc = 111025
              errClone.severity = 10
              requirejs.onError(errClone)
            }, requirejs.onError)
          }
        }

    const buildFunctionParametersObject = (pkgs, args) =>
      pkgs.reduce((result, pkg, index) => {
        result[pkg] = args[index]
        return result
      }, {})


    const loadRequirements = (pkgs, cb) => {
      const loaded = cb ?
        (...args) => {
          const p = buildFunctionParametersObject(pkgs, args)
          cb(p)
        } : () => {}

      if (queryUtil.getParameterByName('debug')) {
        requirejs(pkgs, loaded)
      } else {
        requirejs(pkgs, loaded, handleRequireError(pkgs))
      }
    }

    const load = (pkgs, cb) => {
      wixBiSession.packagesLoaded = Date.now()

      /*
       * Require all needed packages (static + conditional)
       * Then do initial site render (or re-layout if it was rendered by the server)
       */
      function getAjaxHandler($) {
        const ignore = {}

        const canUseCache = ({url, type}) => !ignore[url] && (!type || type.toUpperCase() === 'GET')

        return function (options) {
          if (options.cache !== false && canUseCache(options) && contentCache(options)) {
            return
          }
          ignore[options.url] = true
          $.ajax.apply($, arguments)
        }
      }

      loadRequirements([...pkgs, 'coreUtils', 'zepto'], p => {
        const ajaxHandler = getAjaxHandler(p.zepto)
        p.coreUtils.ajaxLibrary.register(ajaxHandler)

        if (siteModel.wixData) {
          const siteStructureNode = window.document.getElementById('masterPage') || window.document.getElementById('SITE_STRUCTURE')
          siteModel.wixHtmlRaw = siteStructureNode.outerHTML
          siteModel.wixAnchors = window.anchors || {}
          siteStructureNode.parentNode.removeChild(siteStructureNode)
        }

        siteModel.requestModel = {
          userAgent: window.navigator.userAgent,
          cookie: window.document.cookie,
          storage: p.coreUtils.storage(window)
        }
        siteModel.currentUrl = p.coreUtils.urlUtils.parseUrl(window.serverSideUrl || window.location.href)
        siteModel.forceMobileView = window.forceMobileView

        cb(p, ajaxHandler)
      })
    }

    const loadWithIntegrationPackages = (pkgs, cb) => {
      const addIntegrationPackages = packages => {
        if (isQaAutomation) {
          packages.push('qaAutomation')
        }
        if (isTpaIntegration) {
          packages.push('tpaIntegration', 'jasmine', 'jasmine-html')
        }
        if (isWixCodeIntegration) {
          packages.push('wixCodeIntegration', 'jasmine', 'jasmine-html')
        }
        return packages
      }

      const initIntegrationPackages = p => {
        const {tpaIntegration, wixCodeIntegration} = p
        if (tpaIntegration) {
          tpaIntegration.init(window, p.core)
        }
        if (wixCodeIntegration) {
          wixCodeIntegration.init(window, p.core, p.wixCode, p.widgets, p.utils, p.tpa)
        }
      }

      loadRequirements(addIntegrationPackages(pkgs), p => {
        window.Promise = window.Promise || p.bluebird
        initIntegrationPackages(p)
        cb(p)
      })
    }

    function getClientSidePackages() {
      const clientSidePackages = ['lodash', 'zepto', 'coreUtilsLib', 'imageClientLib', 'imageClientApi', 'multilingual', 'coreUtils',
        'core', 'layout', 'skins', 'react', 'TweenMax', 'components', 'mobx', 'mobx-react', 'color',
        'remoteDOM', 'TimelineMax', 'animations', 'tpa', 'dataFixer', 'siteUtils', 'image',
        'translationsUtils', 'hammer', 'utils', 'xss', 'platformUtils', 'loggingUtils',
        'swfobject', 'santaProps', 'displayer', 'mousetrap', 'backgroundCommon',
        'DrawSVGPlugin', 'reactDOM', 'ScrollToPlugin', 'widgets', 'experiment', 'reactDOMServer',
        'imageCommon', 'formCommon', 'textCommon', 'containerCommon', 'socialCommon', 'galleriesCommon', 'buttonCommon',
        'compDesignUtils', 'audioCommon', 'skinExports', 'compUtils', 'mobileLayoutUtils', 'imageZoom',
        'mobileLayoutUtils', 'pmrpc', 'tweenEngine', 'render'
      ]
      return addConditionalDependencies(clientSidePackages)
    }

    function renderClientSide() {
      const clientSidePackages = getClientSidePackages()

      loadRequirements(['lodash', 'wixCodeInit'], ({wixCodeInit}) => {
        performance.mark('code init start')

        const wixCodeAppApi = wixCodeInit.getAppApi()
        wixCodeInit.initMainR(wixCodeAppApi, undefined, siteModel, mobileView, queryUtil, undefined, isBot)
        const props = {
          wixCodeAppApi
        }
        const notify = notifyParent.bind(null, 'santaReady')

        const corePlus = ['core']
        if (isQaAutomation) {
          corePlus.push('qaAutomation')
        }

        function isProtectedPage() {
          const {pageId: currentPageId} = pageInfo
          if (!currentPageId) {
            return false
          }
          const {publicModel: {pageList: {pages = []} = {}} = {}} = siteModel
          const pageData = pages.reduce((res, page) => page.pageId === currentPageId ? page : res) //eslint-disable-line no-confusing-arrow
          return pageData ? !pageData.pageJsonFileName : false
        }

        load(corePlus, (pkgs, ajaxHandler) => {
          performance.mark('prepare start')

          const requireAllComponents =
            !experiment.isOpen('selectiveDownload') ||
            isPreview ||
            useDS ||
            isExternalPreview ||
            !window.Promise ||
            isProtectedPage()

          const {core, qaAutomation} = pkgs
          if (qaAutomation) {
            qaAutomation.init(window, siteModel)
          }

          if (requireAllComponents) {
            loadRequirements(['components'].concat(core.requireCompCode.all()), () => {
              if (useDS || isPreview || isExternalPreview) {
                clientSidePackages.push('componentsPreviewLayer')
              }
              loadWithIntegrationPackages(clientSidePackages, p => {
                p.render.renderClientSide(p, ajaxHandler, siteModel, props, notify)
              })
            })
          } else {
            siteModel.selectiveCompDownload = true
            const privates = core.renderer.createSitePrivatesEx(ajaxHandler, siteModel)
            const prepared = core.renderer.prepareSiteForRender(privates.displayedSiteData, privates.viewerPrivateServices, props)
            loadWithIntegrationPackages(clientSidePackages, p => {
              p.render.renderFromPrepared(p, privates, prepared, props, notify)
            })
          }
        })

        miniPackages(window, requirejs, isServerSide)

        performance.mark('code init finish')
        performance.measure('code init', 'code init start', 'code init finish')
      })

      loadRequirements(clientSidePackages)
    }

    function renderServerSide() {
      load(['lodash', 'coreUtilsLib', 'loggingUtils', 'siteUtils', 'experiment', 'imageClientApi',
        'mobileLayoutUtils',
        'imageClientLib', 'skinExports', 'layout', 'warmup', ...window.Promise ? [] : ['bluebird']], loadedPackages => {
        window.Promise = window.Promise || loadedPackages.bluebird

        loadedPackages.warmup.init(siteModel, () => {
          notifyParent('santaDidLayout')
          renderClientSide()
        })
      })
    }

    // Server side render state
    window.sssr = {}

    function callRender() {
      if (isServerSide) {
        renderServerSide()
      } else {
        renderClientSide()
      }
    }

    if (isLocal(window.santaBase) && queryUtil.getParameterByName('hot') !== 'false') {
      requirejs([
        'hot',
        'zepto'
      ], (
        hot,
        $
      ) => {
        hot.init($)
        callRender()
      })
    } else {
      callRender()
    }
  }

  return render
}



// WEBPACK FOOTER //
// ./~/santa-main-r/src/lib/viewer/render.js